/**
 * MA-Zuteilung (Assignment) field + cross-field validation (OU-1g.b).
 *
 * Lives in `/shared` (CLAUDE.md §26) so the admin/CLI import path and the
 * authoritative backend routes validate against ONE schema.
 *
 * `period` is the kontextlose Quartals-Kennung "Q1"–"Q4" (Decision #1); the
 * Schuljahr + Halbjahr kommen aus `Offering.termLabel`. The cross-field rule
 * (Decision #2): Q1/Q2 dürfen nur an einem Offering mit termLabel "…-1" hängen,
 * Q3/Q4 nur an "…-2" — `validatePeriodMatchesTermLabel` enforces it and throws
 * `AssignmentError` (mapped onto the canonical error body by the backend's
 * `appErrorHandler` via `statusCode`/`code`).
 */
import { z } from 'zod';
export const PERIOD_REGEX = /^Q[1-4]$/;
/**
 * Domain error for assignment validation + service operations (OU-1g.b).
 *
 * Carries `statusCode`/`code` (+ optional `field`) so the backend's global
 * `appErrorHandler` maps it onto `{ error: { code, message, field? } }` — same
 * contract as `OfferingError`/`AssessmentDisabledError`. Defined in `/shared`
 * (not the backend service) because the cross-field validator below — used by
 * both the import service and the manual-create route — must be able to throw it.
 */
export class AssignmentError extends Error {
    statusCode;
    code;
    field;
    constructor(code, statusCode, message, field) {
        super(message);
        this.name = 'AssignmentError';
        this.code = code;
        this.statusCode = statusCode;
        this.field = field;
    }
}
/** Q1/Q2 → 1. Halbjahr, Q3/Q4 → 2. Halbjahr. Throws on an out-of-range period. */
export function quarterToHalbjahr(period) {
    switch (period) {
        case 'Q1':
        case 'Q2':
            return 1;
        case 'Q3':
        case 'Q4':
            return 2;
        default:
            throw new AssignmentError('PERIOD_INVALID', 400, `Ungültiges Quartal "${period}". Erlaubt: Q1–Q4.`, 'period');
    }
}
/**
 * Cross-field rule (Decision #2): the period's Halbjahr must match the term's
 * Halbjahr suffix. "Q1"/"Q2" only against a termLabel ending in "-1", "Q3"/"Q4"
 * only against "-2". Throws `AssignmentError('PERIOD_TERMLABEL_MISMATCH')`.
 */
export function validatePeriodMatchesTermLabel(period, termLabel) {
    const expected = quarterToHalbjahr(period);
    // termLabel format is "YYYY/YY-N" (TERM_LABEL_REGEX) — the suffix carries N.
    const actual = Number(termLabel.slice(-1));
    if (actual !== expected) {
        throw new AssignmentError('PERIOD_TERMLABEL_MISMATCH', 400, `Quartal ${period} gehört ins ${expected}. Halbjahr, das Angebot liegt aber im Halbjahr "${termLabel}".`, 'period');
    }
}
const idField = z.number().int().positive();
const periodField = z.string().regex(PERIOD_REGEX, 'Quartal muss "Q1"–"Q4" sein.');
/** Manual single-assignment create (prepared for OU-1h, scharf seit 1g.b). */
export const AssignmentCreateSchema = z.object({
    studentId: idField,
    offeringId: idField,
    period: periodField,
    correctorTeacherId: idField.nullable().optional(),
});
/** Partial PATCH — corrector, period and offering are editable. */
export const AssignmentUpdateSchema = z.object({
    offeringId: idField.optional(),
    period: periodField.optional(),
    correctorTeacherId: idField.nullable().optional(),
});
//# sourceMappingURL=assignment-validation.js.map